import {Injectable} from '@angular/core';
import {Location} from '@angular/common';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {SmConfirmationDialogService} from '../shared/components/sm-confirmation-dialog/sm-confirmation-dialog.service';
import {AppLanguageService} from '../shared/i18n/app-language.service';
import {
  buildPlatformAwareRoutePathname,
  stripLocalizedRouteLanguageFromPathname,
} from '../shared/i18n/app-language';
import {AuthService} from './auth.service';
import {safeAuthReturnUrl} from './auth-return-url';

function isLoginRoute(url: string): boolean {
  let pathname = url.split(/[?#]/, 1)[0];

  try {
    pathname = new URL(url, 'http://localhost').pathname;
  } catch {
    // Angular router URLs are normally valid; keep the path-only fallback for malformed input.
  }

  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  return stripLocalizedRouteLanguageFromPathname(normalizedPathname) === '/login';
}

@Injectable({
  providedIn: 'root',
})
export class AuthSessionInvalidationService {
  private invalidationPromise: Promise<boolean> | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly confirmationDialogService: SmConfirmationDialogService,
    private readonly appLanguageService: AppLanguageService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly location: Location,
  ) {}

  handleInvalidSession(): Promise<void> {
    if (this.invalidationPromise) {
      return this.invalidationPromise.then(() => void 0);
    }

    const currentUrl = this.location.path(true) || this.router.url;
    const returnUrl = safeAuthReturnUrl(currentUrl);
    this.authService.clearSession();

    const loginRoute = buildPlatformAwareRoutePathname('/login', this.appLanguageService.languageCode);
    const loginPath = returnUrl ? `${loginRoute}?${new URLSearchParams({returnUrl})}` : loginRoute;
    if (isLoginRoute(currentUrl)) {
      return Promise.resolve();
    }

    this.invalidationPromise = this.confirmationDialogService.open({
      title: this.translateService.instant('auth.sessionInvalidation.title'),
      description: this.translateService.instant('auth.sessionInvalidation.description'),
      cancelLabel: this.translateService.instant('auth.sessionInvalidation.okLabel'),
      confirmLabel: this.translateService.instant('auth.sessionInvalidation.okLabel'),
      closeLabel: this.translateService.instant('auth.sessionInvalidation.okLabel'),
      dialogType: 'information',
      confirmTone: 'primary',
      contentAlign: 'center',
      actionsAlign: 'center',
      showCloseButton: false,
      showCancelButton: false,
      onCancel: () => {
        this.navigateToLogin(loginPath);
      },
      onConfirm: () => {
        this.navigateToLogin(loginPath);
      },
    }).finally(() => {
      this.invalidationPromise = null;
    });

    return this.invalidationPromise.then(() => void 0);
  }

  private navigateToLogin(loginPath: string): void {
    if (typeof window !== 'undefined' && typeof window.location.replace === 'function') {
      window.location.replace(loginPath);
      return;
    }

    void this.router.navigateByUrl(loginPath, {replaceUrl: true});
  }
}
